import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

export interface PdfAiAnalyzeOptions {
  modelId?: string;                         // DI model: 'prebuilt-document' or your custom model id
  schemaHint?: Record<string, any>;         // Optional for AOAI endpoint
  onProgress?: (pct: number) => void;       // Upload progress callback
}

export interface DiAnalyzeResult {
  raw: any;
  byKey?: Record<string, string | number | null>;
  tables?: Array<Array<Record<string, string>>>;
}

export interface AoaiExtractResult {
  raw: any;
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class PdfAiService {
  private readonly apiBase = '/api'; // adjust if you’re not proxying

  constructor(private http: HttpClient) { }

  /** Azure Document Intelligence — with upload progress */
  async analyzeWithDocumentIntelligence(file: File, opts?: PdfAiAnalyzeOptions): Promise<DiAnalyzeResult> {
    const form = new FormData();
    form.append('file', file);
    if (opts?.modelId) form.append('modelId', opts.modelId);

    const req$ = this.http.post<DiAnalyzeResult>(
      `${this.apiBase}/di/analyze`,
      form,
      { observe: 'events', reportProgress: true }
    );

    const final$ = req$.pipe(
      // emit progress to caller
      tap((ev: HttpEvent<DiAnalyzeResult>) => {
        if (ev.type === HttpEventType.UploadProgress && typeof opts?.onProgress === 'function') {
          const loaded = (ev as any).loaded as number | undefined;
          const total = (ev as any).total as number | undefined;
          const pct = total ? Math.round((100 * (loaded ?? 0)) / total) : 0;
          opts.onProgress(pct);
        }
      }),
      // keep only the terminal response event
      filter(ev => ev.type === HttpEventType.Response),
      // map to typed body
      map(ev => (ev as any).body as DiAnalyzeResult)
    );

    return await firstValueFrom(final$);
  }

  /** Azure OpenAI (optional) — with upload progress */
  async extractWithAzureOpenAI(file: File, opts?: PdfAiAnalyzeOptions): Promise<AoaiExtractResult> {
    const form = new FormData();
    form.append('file', file);
    if (opts?.schemaHint) {
      form.append('schemaHint', new Blob([JSON.stringify(opts.schemaHint)], { type: 'application/json' }));
    }

    const req$ = this.http.post<AoaiExtractResult>(
      `${this.apiBase}/aoai/extract`,
      form,
      { observe: 'events', reportProgress: true }
    );

    const final$ = req$.pipe(
      tap((ev: HttpEvent<AoaiExtractResult>) => {
        if (ev.type === HttpEventType.UploadProgress && typeof opts?.onProgress === 'function') {
          const loaded = (ev as any).loaded as number | undefined;
          const total = (ev as any).total as number | undefined;
          const pct = total ? Math.round((100 * (loaded ?? 0)) / total) : 0;
          opts.onProgress(pct);
        }
      }),
      filter(ev => ev.type === HttpEventType.Response),
      map(ev => (ev as any).body as AoaiExtractResult)
    );

    return await firstValueFrom(final$);
  }
}
